import React, { useState } from 'react';
import { useTranslations } from 'src/contexts/TranslationContext';
import CommonPage from 'src/components/CommonPage';

interface ContactProps {
  lang?: string;
}

const Contact: React.FC<ContactProps> = ({ lang }) => {
  const { translations: t } = useTranslations();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError('');
    
    try {
      // Here you would typically send the form data to your backend
      // For now, we'll just simulate a successful submission
      await new Promise(resolve => setTimeout(resolve, 1000));
      setSubmitSuccess(true);
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      setSubmitError('An error occurred while submitting the form. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const content = (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
      <div>
        <h2 className="text-xl font-semibold mb-4">{t?.navigations?.contact?.title || 'Get in Touch'}</h2>
        <p className="mb-4">
          {t?.navigations?.about?.description || 
            'Global24 is your source for the latest news and updates from around the world.'}
        </p>
        
        <div className="mb-6">
          <h3 className="font-semibold mb-2">{t?.navigations?.contact?.location || 'Our Location'}</h3>
          <p>New York, NY, USA</p>
        </div>
        
        <div className="mb-6">
          <h3 className="font-semibold mb-2">Email</h3>
          <p>info@global24.news</p>
        </div>
        
        <div>
          <h3 className="font-semibold mb-2">{t?.navigations?.footer?.followUs || 'Follow Us'}</h3>
          <div className="flex space-x-4">
            <a href="#" className="text-blue-600 hover:text-blue-800">Facebook</a>
            <a href="#" className="text-blue-400 hover:text-blue-600">Twitter</a>
            <a href="#" className="text-pink-600 hover:text-pink-800">Instagram</a>
          </div>
        </div>
      </div>
      
      <div>
        <h2 className="text-xl font-semibold mb-4">{t?.navigations?.contact?.title || 'Send Us a Message'}</h2>
        
        {submitSuccess ? (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
            <p>Thank you for your message! We will get back to you soon.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {submitError && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                <p>{submitError}</p>
              </div>
            )}
            
            <div>
              <label htmlFor="name" className="block mb-1">
                {t?.navigations?.contact?.name || 'Your Name'}
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div>
              <label htmlFor="email" className="block mb-1">
                {t?.navigations?.contact?.email || 'Email'}
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div>
              <label htmlFor="subject" className="block mb-1">
                {t?.navigations?.contact?.subject || 'Subject'}
              </label>
              <input
                type="text"
                id="subject"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div>
              <label htmlFor="message" className="block mb-1">
                {t?.navigations?.contact?.message || 'Message'}
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                rows={5}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              ></textarea>
            </div>
            
            <button
              type="submit"
              disabled={isSubmitting}
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
            >
              {isSubmitting ? 'Sending...' : (t?.navigations?.contact?.submit || 'Send Message')}
            </button>
          </form>
        )}
      </div>
    </div>
  );

  return (
    <CommonPage
      lang={lang}
      pageType="contact"
      title="Contact Us"
      content={content}
    />
  );
};

export default Contact; 